import { Trash2 } from 'lucide-react'
import { useState } from 'react'
import { Modal } from '@/components/ui/modal'
import type { QueueStats } from '@/lib/types'

interface QueuePanelProps {
  stats: QueueStats | undefined
  isLoading: boolean
  onFlush: () => void
  isFlushing: boolean
}

export function QueuePanel({
  stats,
  isLoading,
  onFlush,
  isFlushing,
}: QueuePanelProps): React.JSX.Element {
  const [showConfirm, setShowConfirm] = useState(false)

  const sortedDomains = stats ? Object.entries(stats.by_domain).sort(([, a], [, b]) => b - a) : []

  function handleConfirmFlush(): void {
    onFlush()
    setShowConfirm(false)
  }

  return (
    <div className="rounded-xl border border-surface-3 bg-surface-0 p-6">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-medium text-text-primary">Queue Stats</h2>
          <p className="text-xs text-text-tertiary">Messages waiting by domain</p>
        </div>
        <button
          type="button"
          onClick={() => setShowConfirm(true)}
          disabled={isFlushing || !stats || stats.total === 0}
          className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-red-700 disabled:opacity-40"
        >
          <Trash2 className="h-3.5 w-3.5" />
          Flush Queue
        </button>
      </div>

      {isLoading || !stats ? (
        <div className="flex h-32 items-center justify-center">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-admin-500 border-t-transparent" />
        </div>
      ) : sortedDomains.length === 0 ? (
        <div className="flex h-32 items-center justify-center">
          <p className="text-sm text-text-tertiary">Queue is empty</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-surface-3">
                <th className="pb-2 text-left text-xs font-medium uppercase text-text-secondary">
                  Domain
                </th>
                <th className="pb-2 text-right text-xs font-medium uppercase text-text-secondary">
                  Depth
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-2">
              {sortedDomains.map(([domain, depth]) => (
                <tr key={domain}>
                  <td className="py-2 font-medium text-text-primary">{domain}</td>
                  <td className="py-2 text-right text-text-secondary">{depth.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-surface-3">
                <td className="pt-2 text-xs font-medium text-text-secondary">Total</td>
                <td className="pt-2 text-right text-xs font-medium text-text-primary">
                  {stats.total.toLocaleString()}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <Modal open={showConfirm} onClose={() => setShowConfirm(false)} title="Flush Queue" size="sm">
        <p className="text-sm text-text-secondary">
          This will flush all{' '}
          <span className="font-semibold text-text-primary">
            {stats?.total.toLocaleString() ?? 0}
          </span>{' '}
          messages from the queue. Are you sure?
        </p>
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={() => setShowConfirm(false)}
            className="rounded-lg border border-surface-3 px-4 py-2 text-sm text-text-secondary hover:bg-surface-1"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirmFlush}
            disabled={isFlushing}
            className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
          >
            {isFlushing ? 'Flushing...' : 'Flush All Messages'}
          </button>
        </div>
      </Modal>
    </div>
  )
}
